import { Link, Typography } from "@mui/material";
import { parseISO } from "date-fns";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import type { Maybe } from "@entrilia/shared/types";
import { formatDateTime } from "@entrilia/shared/utilities/dateUtils";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import { parseUrlFromMarkdown } from "@entrilia/shared/utilities/urlHelper";
import type {
  EntityFieldConfiguration,
  EntityFieldType,
  LookupObjectReference,
  ObjectTypeToLookupReferences,
} from "../../api/types/objectTypes";
import useClientContext from "../../context/useClientContext";
import { getDictionaryDisplayNameFromCode } from "../../utilities/dictionariesHelper";
import UrlFieldLink from "./UrlFieldLink";
import {
  formatDateFieldValue,
  formatNumericFieldValue,
  getLookupOptionDisplayValue,
  getMultiselectOptionsFromValue,
} from "./helpers";

interface Props {
  type: EntityFieldType;
  configuration?: EntityFieldConfiguration;
  lookupObjectReferences?: LookupObjectReference[];
  lookupOptionsByObjectType?: ObjectTypeToLookupReferences;
  value: unknown;
  wrapTextValue?: boolean;
  hideFullTextTooltip?: boolean;
}

const EntityValueGridCell = ({
  type,
  configuration,
  lookupObjectReferences,
  lookupOptionsByObjectType,
  value,
  wrapTextValue,
  hideFullTextTooltip,
}: Props) => {
  const { dictionaries } = useClientContext();

  const ellipsisTooltipProps = hideFullTextTooltip ? { title: "" } : undefined;

  switch (type) {
    case "Email": {
      return (
        <Link
          href={`mailto:${stringifyValue(value)}`}
          underline="hover"
          target="_blank"
          onClick={(e) => e.stopPropagation()}
        >
          {wrapTextValue ? (
            <Typography>stringifyValue(value)</Typography>
          ) : (
            <TypographyTooltipEllipsis text={stringifyValue(value)} tooltipProps={ellipsisTooltipProps} />
          )}
        </Link>
      );
    }
    case "Url": {
      const { title, url } = parseUrlFromMarkdown(stringifyValue(value));
      if (!url) {
        return null;
      }
      const displayText = title.length > 50 ? title.slice(0, 50) + "…" : title;
      return <UrlFieldLink title={displayText} url={url} noWrap />;
    }
    case "Select": {
      if (!value) {
        return null;
      }
      const optionFieldType = configuration?.$type === "Select" && configuration.fieldType;
      const resolvedValue =
        (optionFieldType && getDictionaryDisplayNameFromCode(stringifyValue(value), optionFieldType, dictionaries)) ||
        stringifyValue(value);
      return wrapTextValue ? (
        <Typography>{resolvedValue}</Typography>
      ) : (
        <TypographyTooltipEllipsis text={resolvedValue} tooltipProps={ellipsisTooltipProps} />
      );
    }
    case "UserDefinedOptionsSelect": {
      if (!value) {
        return null;
      }
      const configurationOptions =
        (configuration?.$type === "UserDefinedOptionsSelect" && configuration.userDefinedOptions) || [];
      const resolvedValue =
        configurationOptions.find((option) => option.value === value)?.label || stringifyValue(value);
      return wrapTextValue ? (
        <Typography>{resolvedValue}</Typography>
      ) : (
        <TypographyTooltipEllipsis text={resolvedValue} tooltipProps={ellipsisTooltipProps} />
      );
    }
    case "MultiSelect": {
      const optionValues = getMultiselectOptionsFromValue(value as Maybe<string | string[]>);
      const resolvedValue = optionValues.join(", ");
      return wrapTextValue ? (
        <Typography>{resolvedValue}</Typography>
      ) : (
        <TypographyTooltipEllipsis text={resolvedValue} tooltipProps={ellipsisTooltipProps} />
      );
    }
    case "UserDefinedOptionsMultiSelect": {
      const optionValues = getMultiselectOptionsFromValue(value as Maybe<string | string[]>);
      const configurationOptions =
        (configuration?.$type === "UserDefinedOptionsSelect" && configuration.userDefinedOptions) || [];
      const displayValues = optionValues.map(
        (optionValue) =>
          configurationOptions.find((configOption) => configOption.value === optionValue)?.label ?? optionValue
      );
      const resolvedValue = displayValues.join(", ");
      return wrapTextValue ? (
        <Typography>{resolvedValue}</Typography>
      ) : (
        <TypographyTooltipEllipsis text={resolvedValue} tooltipProps={ellipsisTooltipProps} />
      );
    }
    case "Date": {
      if (!value) {
        return null;
      }

      const dateFieldConfiguration = configuration?.$type === "Date" ? configuration : undefined;
      const dateValue = parseISO(stringifyValue(value));
      return <Typography>{formatDateFieldValue(dateValue, dateFieldConfiguration)}</Typography>;
    }
    case "DateTime": {
      if (!value) {
        return null;
      }

      const dateValue = parseISO(stringifyValue(value));
      return <Typography>{formatDateTime(dateValue)}</Typography>;
    }
    case "Number":
    case "Money":
    case "Percent": {
      if (typeof value !== "number" || Number.isNaN(value)) {
        return null;
      }

      return <Typography>{formatNumericFieldValue(value, configuration)}</Typography>;
    }
    case "Checkbox": {
      return value === true ? "Yes" : value === false ? "No" : null;
    }
    case "Lookup": {
      const objectId = value;
      if (typeof objectId !== "string") {
        return null;
      }

      const lookupConfiguration = configuration?.$type === "Lookup" ? configuration : undefined;

      const lookupReferencesFromOptions =
        lookupConfiguration && lookupOptionsByObjectType
          ? (lookupOptionsByObjectType[lookupConfiguration.objectType] ?? [])
          : [];

      const resolvedValue = getLookupOptionDisplayValue(objectId, [
        ...(lookupObjectReferences ?? []),
        ...lookupReferencesFromOptions,
      ]);

      if (!resolvedValue) {
        return null;
      }

      return wrapTextValue ? (
        <Typography>{resolvedValue}</Typography>
      ) : (
        <TypographyTooltipEllipsis text={resolvedValue} tooltipProps={ellipsisTooltipProps} />
      );
    }
    default: {
      return value ? (
        wrapTextValue ? (
          <Typography>{stringifyValue(value)}</Typography>
        ) : (
          <TypographyTooltipEllipsis text={stringifyValue(value)} tooltipProps={ellipsisTooltipProps} />
        )
      ) : null;
    }
  }
};

export default EntityValueGridCell;
